import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type React from 'react';
import type { ReactNode } from 'react';
import { NAVY, RADIUS_CARD, SURFACE_CHAT, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';

interface ChatPanelProps {
  children: ReactNode;
}

const ChatPanelRoot: React.FC<ChatPanelProps> = ({ children }) => {
  return (
    <Box
      sx={{
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: SURFACE_CONTAINER_LOWEST,
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

interface ChatPanelHeaderProps {
  title: string;
  subtitle?: string;
  leftAction?: ReactNode;
  rightAction?: ReactNode;
}

const ChatPanelHeader: React.FC<ChatPanelHeaderProps> = ({ title, subtitle, leftAction, rightAction }) => {
  return (
    <Box
      sx={{
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {leftAction && (
        <Box
          sx={{
            position: 'absolute',
            left: 8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          {leftAction}
        </Box>
      )}
      <Box sx={{ flex: 1, textAlign: 'center' }}>
        <Typography
          variant="h6"
          sx={{
            color: NAVY,
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body2"
            sx={{
              color: NAVY,
              opacity: 0.55,
              fontFamily: 'Jost, sans-serif',
              fontSize: '12px',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {rightAction && (
        <Box
          sx={{
            position: 'absolute',
            right: 8,
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        >
          {rightAction}
        </Box>
      )}
    </Box>
  );
};

interface ChatPanelBodyProps {
  children: ReactNode;
}

const ChatPanelBody: React.FC<ChatPanelBodyProps> = ({ children }) => {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        mx: 2,
        mb: 2,
        backgroundColor: SURFACE_CHAT,
        borderRadius: RADIUS_CARD,
        overflow: 'hidden',
      }}
    >
      {children}
    </Box>
  );
};

type ChatPanelComponent = React.FC<ChatPanelProps> & {
  Header: typeof ChatPanelHeader;
  Body: typeof ChatPanelBody;
};

const ChatPanel = ChatPanelRoot as ChatPanelComponent;
ChatPanel.Header = ChatPanelHeader;
ChatPanel.Body = ChatPanelBody;

export default ChatPanel;
